import { ago } from "../time";
import type { Attendee, Chat, Message } from "../types/messaging";
import { hash, int, pick, rng } from "./random";

/**
 * The conversations.
 *
 * The threads with a full message list are hand-written: they are what the
 * marketing screenshots and the docs examples show, so they have to read like
 * real work rather than lorem. The rest of each account's chat list is
 * generated from a seeded stream, so the list looks inhabited and is identical
 * on every machine.
 */

const SELF: Record<string, Attendee> = {
  acc_gm_maya: {
    id: "self_li_maya",
    name: "Maya Rao",
    handle: "maya-rao",
    isSelf: true,
  },
  acc_gc_maya: {
    id: "self_wa_maya",
    name: "Maya Rao",
    handle: "+447700900182",
    isSelf: true,
  },
  acc_fm_lena: {
    id: "self_tg_lena",
    name: "Lena Fischer",
    handle: "@lenafischer",
    isSelf: true,
  },
  acc_gm_sofia: {
    id: "self_li_sofia",
    name: "Sofia Marchetti",
    handle: "sofia-marchetti",
    isSelf: true,
  },
};

function attendee(
  id: string,
  name: string,
  handle: string,
  headline?: string,
): Attendee {
  return { id, name, handle, headline };
}

interface Authored {
  chat: Omit<Chat, "lastMessage" | "updatedAt">;
  messages: Omit<Message, "chatId" | "accountId">[];
}

/** The hand-written threads, newest message last in each. */
const AUTHORED: Authored[] = [
  {
    chat: {
      id: "cht_li_devika",
      accountId: "acc_gm_maya",
      provider: "linkedin",
      kind: "direct",
      name: "Devika Menon",
      attendees: [
        attendee(
          "att_devika",
          "Devika Menon",
          "devika-menon",
          "VP Revenue Operations at Lumen Freight",
        ),
        SELF.acc_gm_maya as Attendee,
      ],
      unread: 2,
      muted: false,
      archived: false,
    },
    messages: [
      {
        id: "msg_dv_1",
        direction: "outbound",
        senderId: "self_li_maya",
        senderName: "Maya Rao",
        text: "Devika — saw your post on consolidating the five tools your SDRs juggle. We solved exactly that for Lumen's peer in the 3PL space. Worth 15 minutes?",
        at: ago(4, "d"),
        status: "read",
        attachments: [],
        reactions: [],
        viaApi: true,
      },
      {
        id: "msg_dv_2",
        direction: "inbound",
        senderId: "att_devika",
        senderName: "Devika Menon",
        text: "Hi Maya. Timing is decent — we're mid-way through an RFP. What does your side of the consolidation actually replace?",
        at: ago(3, "d"),
        status: "read",
        attachments: [],
        reactions: [],
      },
      {
        id: "msg_dv_3",
        direction: "outbound",
        senderId: "self_li_maya",
        senderName: "Maya Rao",
        text: "The sequencer, the LinkedIn automation, and the inbox sync. One contract, one audit trail. I'll send the one-pager we gave the RFP committee at Keystone.",
        at: ago(3, "d"),
        status: "read",
        attachments: [
          {
            id: "atch_dv_1",
            kind: "file",
            name: "northwind-consolidation.pdf",
            size: 486_221,
            mimeType: "application/pdf",
          },
        ],
        reactions: [],
        viaApi: true,
      },
      {
        id: "msg_dv_4",
        direction: "inbound",
        senderId: "att_devika",
        senderName: "Devika Menon",
        text: "Read it on the train. The audit trail is the part my security team will care about — do you have SOC 2?",
        at: ago(5, "h"),
        status: "delivered",
        attachments: [],
        reactions: [{ emoji: "👍", by: ["self_li_maya"] }],
      },
      {
        id: "msg_dv_5",
        direction: "inbound",
        senderId: "att_devika",
        senderName: "Devika Menon",
        text: "Also — can you do Thursday 3pm CET? I'll bring our RevOps lead.",
        at: ago(22, "m"),
        status: "delivered",
        attachments: [],
        reactions: [],
      },
    ],
  },
  {
    chat: {
      id: "cht_wa_brightpath",
      accountId: "acc_gc_maya",
      provider: "whatsapp",
      kind: "group",
      name: "Brightpath · Rollout",
      attendees: [
        attendee("att_jorge", "Jorge Silva", "+5511987654321"),
        attendee("att_ana", "Ana Beltran", "+34600112233"),
        attendee("att_kim", "Kim Ndlovu", "+27821234567"),
        SELF.acc_gc_maya as Attendee,
      ],
      unread: 1,
      muted: false,
      archived: false,
    },
    messages: [
      {
        id: "msg_bp_1",
        direction: "inbound",
        senderId: "att_jorge",
        senderName: "Jorge Silva",
        text: "Morning all. São Paulo warehouse is live on the new flow as of 06:00.",
        at: ago(8, "h"),
        status: "read",
        attachments: [],
        reactions: [{ emoji: "🎉", by: ["att_ana", "att_kim"] }],
      },
      {
        id: "msg_bp_2",
        direction: "inbound",
        senderId: "att_ana",
        senderName: "Ana Beltran",
        text: "Madrid too. Two drivers needed a re-scan but it took a minute each.",
        at: ago(7, "h"),
        status: "read",
        attachments: [],
        reactions: [],
      },
      {
        id: "msg_bp_3",
        direction: "outbound",
        senderId: "self_wa_maya",
        senderName: "Maya Rao",
        text: "Perfect. Kim, how are we looking in Johannesburg?",
        at: ago(6, "h"),
        status: "read",
        attachments: [],
        reactions: [],
        viaApi: true,
      },
      {
        id: "msg_bp_4",
        direction: "inbound",
        senderId: "att_kim",
        senderName: "Kim Ndlovu",
        text: "Going live after the 14:00 shift change. Sending the checklist now.",
        at: ago(2, "h"),
        status: "read",
        attachments: [
          {
            id: "atch_bp_1",
            kind: "image",
            name: "jhb-checklist.jpg",
            size: 1_284_332,
            mimeType: "image/jpeg",
          },
        ],
        reactions: [],
      },
      {
        id: "msg_bp_5",
        direction: "inbound",
        senderId: "att_kim",
        senderName: "Kim Ndlovu",
        text: "",
        at: ago(14, "m"),
        status: "delivered",
        attachments: [
          {
            id: "atch_bp_2",
            kind: "voice",
            name: "voice-note.ogg",
            size: 48_112,
            mimeType: "audio/ogg",
            duration: 23,
          },
        ],
        reactions: [],
      },
    ],
  },
  {
    chat: {
      id: "cht_li_arun",
      accountId: "acc_gm_sofia",
      provider: "linkedin",
      kind: "direct",
      name: "Arun Prakash",
      attendees: [
        attendee(
          "att_arun",
          "Arun Prakash",
          "arun-prakash-eng",
          "Staff Platform Engineer at Pallas",
        ),
        SELF.acc_gm_sofia as Attendee,
      ],
      unread: 0,
      muted: false,
      archived: false,
      inmail: true,
    },
    messages: [
      {
        id: "msg_ar_1",
        direction: "outbound",
        senderId: "self_li_sofia",
        senderName: "Sofia Marchetti",
        text: "Arun — you shipped Pallas's multi-region queue migration, which is almost exactly the problem our platform team is hiring for. The role is Staff, fully remote in CET ±3. Open to a conversation?",
        at: ago(6, "d"),
        status: "read",
        attachments: [],
        reactions: [],
        viaApi: true,
      },
      {
        id: "msg_ar_2",
        direction: "inbound",
        senderId: "att_arun",
        senderName: "Arun Prakash",
        text: "Appreciate a message that's actually read my work. I'm not looking right now, but ask me again in the new year — we ship the last region in December.",
        at: ago(5, "d"),
        status: "read",
        attachments: [],
        reactions: [],
      },
      {
        id: "msg_ar_3",
        direction: "outbound",
        senderId: "self_li_sofia",
        senderName: "Sofia Marchetti",
        text: "Noted, and good luck with the last region. I'll come back in January with whatever the team looks like then.",
        at: ago(5, "d"),
        status: "read",
        attachments: [],
        reactions: [{ emoji: "🙏", by: ["att_arun"] }],
        viaApi: true,
      },
    ],
  },
  {
    chat: {
      id: "cht_tg_builders",
      accountId: "acc_fm_lena",
      provider: "telegram",
      kind: "channel",
      name: "Northwind Builders",
      attendees: [
        attendee("att_vik", "Viktor Ilyin", "@vikilyin"),
        attendee("att_mei", "Mei Zhang", "@meizh"),
        SELF.acc_fm_lena as Attendee,
      ],
      unread: 2,
      muted: true,
      archived: false,
    },
    messages: [
      {
        id: "msg_tb_1",
        direction: "inbound",
        senderId: "att_vik",
        senderName: "Viktor Ilyin",
        text: "The new webhook retry policy is live on staging. Dead letters land in the queue after five attempts instead of three.",
        at: ago(1, "d"),
        status: "read",
        attachments: [],
        reactions: [{ emoji: "🚀", by: ["att_mei", "self_tg_lena"] }],
      },
      {
        id: "msg_tb_2",
        direction: "inbound",
        senderId: "att_mei",
        senderName: "Mei Zhang",
        text: "Replay from the dashboard works too. I replayed yesterday's 412 failures and 410 landed.",
        at: ago(5, "h"),
        status: "delivered",
        attachments: [],
        reactions: [],
      },
      {
        id: "msg_tb_3",
        direction: "inbound",
        senderId: "att_vik",
        senderName: "Viktor Ilyin",
        text: "The two that didn't were a 500 on their side. Rechecking at 16:00.",
        at: ago(48, "m"),
        status: "delivered",
        attachments: [],
        reactions: [],
      },
    ],
  },
  {
    chat: {
      id: "cht_li_queued",
      accountId: "acc_gm_maya",
      provider: "linkedin",
      kind: "direct",
      name: "Felix Brandt",
      attendees: [
        attendee(
          "att_felix",
          "Felix Brandt",
          "felix-brandt",
          "Head of Sales at Stadler Logistik",
        ),
        SELF.acc_gm_maya as Attendee,
      ],
      unread: 0,
      muted: false,
      archived: false,
    },
    messages: [
      {
        id: "msg_fx_1",
        direction: "inbound",
        senderId: "att_felix",
        senderName: "Felix Brandt",
        text: "Thanks for the invite, Maya. Send over whatever you have on the inbox sync.",
        at: ago(90, "m"),
        status: "read",
        attachments: [],
        reactions: [],
      },
      {
        id: "msg_fx_2",
        direction: "outbound",
        senderId: "self_li_maya",
        senderName: "Maya Rao",
        text: "On its way, Felix. One question first — are your reps on Outlook or Gmail?",
        at: ago(3, "m"),
        // The account is at 118 of 250 messages but inside its per-minute
        // pacing window, so Aux0 is holding this one rather than letting
        // LinkedIn reject it.
        status: "queued",
        attachments: [],
        reactions: [],
        viaApi: true,
      },
    ],
  },
  {
    chat: {
      id: "cht_wa_failed",
      accountId: "acc_gc_maya",
      provider: "whatsapp",
      kind: "direct",
      name: "+1 646 555 0177",
      attendees: [
        attendee("att_unknown", "+1 646 555 0177", "+16465550177"),
        SELF.acc_gc_maya as Attendee,
      ],
      unread: 0,
      muted: false,
      archived: false,
    },
    messages: [
      {
        id: "msg_fl_1",
        direction: "outbound",
        senderId: "self_wa_maya",
        senderName: "Maya Rao",
        text: "Hi — following up on the quote from Tuesday.",
        at: ago(26, "h"),
        status: "failed",
        attachments: [],
        reactions: [],
        viaApi: true,
        error: "WhatsApp rejected the send: this number is not registered on WhatsApp.",
      },
    ],
  },
];

/** Generated chat-list filler, so each account's list looks inhabited. */
const FILLER_NAMES = [
  "Hiro Tanaka",
  "Claudia Moreau",
  "Sam Okonkwo",
  "Elena Petrova",
  "Yusuf Demir",
  "Grace Lin",
  "Marco Rossi",
  "Nadia Haddad",
  "Oliver Bennett",
  "Ravi Iyer",
  "Thandi Mokoena",
  "Jonas Berg",
  "Priya Nair",
  "Tomás Duarte",
  "Hannah Weiss",
  "Ibrahim Sow",
  "Zoe Caldwell",
  "Kenji Sato",
  "Laura Esposito",
  "Daniel Kovács",
];

const FILLER_LINES = [
  "Sounds good — let me check with the team and come back to you.",
  "Can you resend that with the pricing page attached?",
  "We're reviewing two vendors this quarter. Yours is one of them.",
  "Thanks for the intro. Booking time with my ops lead first.",
  "Not right now, but keep me on the list for Q1.",
  "Who's the right person to talk to about the API?",
  "Forwarding this to procurement today.",
  "The security questionnaire came back clean. Next step?",
  "Got it. I'll reply properly tomorrow morning.",
  "Perfect, that answers it. Speak Thursday.",
];

const FILLER_GROUPS = [
  "Pipeline · EMEA",
  "Deal room · Keystone",
  "Partners",
  "Support escalations",
  "Design review",
];

/**
 * Chat-list rows for an account beyond its hand-written threads. Preview only:
 * opening one in the mock app shows an empty thread, which is correct — the
 * fixtures carry messages for the threads worth showing.
 */
function fillerChats(accountId: string, provider: string, count: number): Chat[] {
  const r = rng(hash(`chats:${accountId}`));
  const self = SELF[accountId];

  return Array.from({ length: count }, (_, index) => {
    const isGroup = provider !== "linkedin" && r() < 0.18;
    const name = isGroup ? pick(r, FILLER_GROUPS) : pick(r, FILLER_NAMES);
    const other = attendee(
      `att_${accountId}_${index}`,
      isGroup ? pick(r, FILLER_NAMES) : name,
      provider === "whatsapp"
        ? `+${int(r, 1, 44)}${int(r, 1000000000, 9999999999)}`
        : `@${name.toLowerCase().replace(/[^a-z]/g, "")}`,
    );
    const minutesAgo = int(r, 35, 60 * 24 * 21);
    const inbound = r() < 0.62;

    return {
      id: `cht_${accountId}_${index}`,
      accountId,
      provider,
      kind: isGroup ? "group" : "direct",
      name,
      attendees: self ? [other, self] : [other],
      lastMessage: {
        text: pick(r, FILLER_LINES),
        at: ago(minutesAgo, "m"),
        direction: inbound ? "inbound" : "outbound",
        from: inbound ? other.name : (self?.name ?? "You"),
      },
      unread: inbound && r() < 0.22 ? int(r, 1, 4) : 0,
      muted: r() < 0.08,
      archived: r() < 0.05,
      updatedAt: ago(minutesAgo, "m"),
    } satisfies Chat;
  });
}

/** How many filler rows each messaging account gets. */
const FILLER_COUNTS: Record<string, number> = {
  acc_gm_maya: 24,
  acc_gc_maya: 18,
  acc_ic_tom: 14,
  acc_fm_lena: 11,
  acc_ol_lena: 9,
  acc_zo_dan: 12,
  acc_gm_sofia: 28,
  acc_gn_sofia: 6,
  acc_ol_omar: 16,
};

function lastOf(messages: Omit<Message, "chatId" | "accountId">[]) {
  const last = messages[messages.length - 1];
  if (!last) throw new Error("An authored thread needs at least one message.");
  return last;
}

/** Every chat: the authored threads first, then each account's filler. */
export const CHATS: Chat[] = [
  ...AUTHORED.map(({ chat, messages }) => {
    const last = lastOf(messages);
    return {
      ...chat,
      lastMessage: {
        text:
          last.text ||
          (last.attachments[0]?.kind === "voice"
            ? "Voice note"
            : (last.attachments[0]?.name ?? "")),
        at: last.at,
        direction: last.direction,
        from: last.senderName,
      },
      updatedAt: last.at,
    } satisfies Chat;
  }),
  ...Object.entries(FILLER_COUNTS).flatMap(([accountId, count]) => {
    const provider = accountId.startsWith("acc_li")
      ? "linkedin"
      : accountId.startsWith("acc_wa")
        ? "whatsapp"
        : "telegram";
    return fillerChats(accountId, provider, count);
  }),
];

/** Messages, keyed by chat id. Only the authored threads have any. */
export const MESSAGES: Map<string, Message[]> = new Map(
  AUTHORED.map(({ chat, messages }) => [
    chat.id,
    messages.map((message) => ({
      ...message,
      chatId: chat.id,
      accountId: chat.accountId,
    })),
  ]),
);

export const CHAT_BY_ID = new Map(CHATS.map((chat) => [chat.id, chat]));

/** The threads the marketing screenshots and the docs examples use. */
export const SHOWCASE_CHAT_IDS = [
  "cht_li_devika",
  "cht_wa_brightpath",
  "cht_li_arun",
  "cht_tg_builders",
] as const;
